import React, { useState } from "react";
import { X, Loader, CheckCircle, Upload } from "lucide-react";
import toast from "react-hot-toast";
import { createOrder } from "../services/orderService";
import { notifyAdminNewOrder, notifyCustomerConfirmation } from "../services/notificationService";
import { createCustomerFromBooking } from "../services/customerService";
import businessConfig from "../config/businessConfig";

// ── Field must live OUTSIDE BookingModal ─────────────────────
// If defined inside, React recreates it on every keystroke →
// input loses focus → mobile keyboard closes after each letter.
function Field({ label, name, type = "text", placeholder, required, form, errors, onChange }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={form[name]}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full px-4 py-3 rounded-xl border text-sm focus:outline-none transition-colors ${
          errors[name]
            ? "border-red-400 bg-red-50 focus:border-red-400"
            : "border-gray-200 focus:border-indigo-400"
        }`}
      />
      {errors[name] && (
        <p className="text-red-500 text-xs mt-1">{errors[name]}</p>
      )}
    </div>
  );
}

export default function BookingModal({ onClose }) {
  const [step,         setStep]         = useState(1);
  const [loading,      setLoading]      = useState(false);
  const [photo,        setPhoto]        = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [savedOrder,   setSavedOrder]   = useState(null);

  const [form, setForm] = useState({
    name:    "",
    email:   "",
    phone:   "",
    address: "",
    service: "",
    date:    "",
    notes:   "",
  });

  const [errors, setErrors] = useState({});

  const today = new Date().toISOString().split("T")[0];

  const selectedService = businessConfig.services.find(
    s => s.name === form.service
  );

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
  }

  function handlePhoto(e) {
    const file = e.target.files[0];
    if (!file) return;
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  }

  function validate() {
    const newErrors = {};
    if (!form.name.trim())    newErrors.name    = "Full name is required";
    if (!form.phone.trim())   newErrors.phone   = "Phone number is required";
    if (!form.address.trim()) newErrors.address = "Pickup address is required";
    if (!form.service)        newErrors.service = "Please select a service";
    if (!form.date)           newErrors.date    = "Please select a pickup date";

    const phoneClean = form.phone.replace(/\D/g, "");
    if (form.phone && phoneClean.length < 10) {
      newErrors.phone = "Enter a valid phone number (at least 10 digits)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const result = await createOrder({
        ...form,
        paymentStatus: "Pay on Pickup",
      });

      if (!result.success) throw new Error(result.error);

      const orderData = { ...form, orderId: result.orderId, docId: result.docId };

      await createCustomerFromBooking(form);
      await notifyAdminNewOrder(orderData);
      await notifyCustomerConfirmation(orderData);

      setSavedOrder(orderData);
      setLoading(false);
      setStep(2);

    } catch (err) {
      setLoading(false);
      toast.error("Booking failed. Please try again.");
      console.error("Booking error:", err);
    }
  }

  // Field is defined outside this component to prevent remount on every keystroke
  // (remounting closes the mobile keyboard after each character)

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md relative max-h-[92vh] overflow-y-auto shadow-2xl">

        {/* ── Header ── */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <div>
            <h3 className="text-lg font-bold text-gray-800">
              {step === 1 ? "📅 Book a Pickup" : "✅ Booking Confirmed"}
            </h3>
            <div className="flex items-center gap-2 mt-1">
              {[1, 2].map(s => (
                <div
                  key={s}
                  className={`h-1.5 w-8 rounded-full transition-colors ${
                    s <= step ? "bg-indigo-600" : "bg-gray-200"
                  }`}
                />
              ))}
              <span className="text-xs text-gray-400">Step {step} of 2</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-xl transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6">

          {/* ── STEP 1: Form ── */}
          {step === 1 && (
            <form onSubmit={handleSubmit} className="space-y-4">

              {/* ── 25 piece minimum notice — door tag ── */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-3">
                <span className="text-xl shrink-0">📦</span>
                <div>
                  <p className="text-amber-800 font-bold text-xs uppercase tracking-wide mb-0.5">Minimum Order Required</p>
                  <p className="text-amber-700 text-xs leading-relaxed">
                    Free pickup &amp; delivery to your door requires a minimum of{" "}
                    <span className="font-black">25 garments</span> per order.{" "}
                    Questions?{" "}
                    <a href={`tel:${businessConfig.phoneDial}`} className="underline font-bold hover:text-amber-900">
                      Call us first.
                    </a>
                  </p>
                </div>
              </div>

              <Field label="Full Name"      name="name"    placeholder="e.g. John Smith"               required form={form} errors={errors} onChange={handleChange} />
              <Field label="Phone Number"   name="phone"   placeholder="e.g. (281) 555-0100" type="tel" required form={form} errors={errors} onChange={handleChange} />
              <Field label="Email Address"  name="email"   placeholder="your@email.com"     type="email"        form={form} errors={errors} onChange={handleChange} />
              <Field label="Pickup Address" name="address" placeholder="e.g. 123 Main St, Houston TX"  required form={form} errors={errors} onChange={handleChange} />

              {/* Service selector */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Service <span className="text-red-500">*</span>
                </label>
                <select
                  name="service"
                  value={form.service}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-xl border text-sm focus:outline-none transition-colors bg-white ${
                    errors.service ? "border-red-400 bg-red-50" : "border-gray-200 focus:border-indigo-400"
                  }`}
                >
                  <option value="">Select a service…</option>
                  {businessConfig.services.filter(s => s.active).map(s => (
                    <option key={s.id} value={s.name}>
                      {s.icon} {s.name} — {s.unit}
                    </option>
                  ))}
                </select>
                {errors.service && (
                  <p className="text-red-500 text-xs mt-1">{errors.service}</p>
                )}
              </div>

              {/* Date picker */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Pickup Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="date"
                  value={form.date}
                  min={today}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-xl border text-sm focus:outline-none transition-colors ${
                    errors.date ? "border-red-400 bg-red-50" : "border-gray-200 focus:border-indigo-400"
                  }`}
                />
                {errors.date && (
                  <p className="text-red-500 text-xs mt-1">{errors.date}</p>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Special Instructions <span className="text-gray-400">(optional)</span>
                </label>
                <textarea
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  placeholder="e.g. Handle with care, stain on left sleeve..."
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-indigo-400 resize-none"
                />
              </div>

              {/* Photo upload */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Garment Photo <span className="text-gray-400">(optional)</span>
                </label>
                <label className="flex items-center gap-3 p-3 rounded-xl border-2 border-dashed border-gray-200 hover:border-indigo-400 cursor-pointer transition-colors">
                  <Upload size={18} className="text-gray-400" />
                  <span className="text-sm text-gray-500">
                    {photo ? photo.name : "Click to upload photo (JPG, PNG — max 5MB)"}
                  </span>
                  <input type="file" accept="image/*" onChange={handlePhoto} className="hidden" />
                </label>
                {photoPreview && (
                  <img src={photoPreview} alt="Preview" className="mt-2 w-full h-32 object-cover rounded-xl border" />
                )}
              </div>

              {/* Service summary */}
              {selectedService && (
                <div className="bg-indigo-50 rounded-xl p-4 flex justify-between items-center">
                  <div>
                    <p className="font-semibold text-sm text-gray-700">{selectedService.name}</p>
                    <p className="text-xs text-gray-500">Ready in {selectedService.duration}</p>
                  </div>
                  <p className="text-sm font-bold text-indigo-600">{selectedService.unit}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-indigo-600 text-white py-3.5 rounded-xl font-bold hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading
                  ? <><Loader size={16} className="animate-spin" /> Processing…</>
                  : "Confirm Booking →"
                }
              </button>

              <p className="text-center text-xs text-gray-400">
                By booking you agree to our terms of service. Your data is safe 🔒
              </p>
            </form>
          )}

          {/* ── STEP 2: Success / Door Tag ── */}
          {step === 2 && (
            <div className="text-center py-6 space-y-4">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <CheckCircle size={40} className="text-green-600" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-green-600">Booking Confirmed!</h3>
                <p className="text-gray-500 text-sm mt-2 leading-relaxed">
                  Hi <strong>{form.name.split(" ")[0]}</strong>, your booking has been received
                  and our team has been notified.
                </p>
              </div>

              {/* ── Door tag: 25 piece minimum reminder ── */}
              <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-4 text-left">
                <p className="font-black text-amber-800 text-sm flex items-center gap-2 mb-1">
                  <span>📦</span> Important Reminder
                </p>
                <p className="text-amber-700 text-xs leading-relaxed">
                  Free pickup &amp; delivery to your door requires a minimum of{" "}
                  <span className="font-black">25 garments</span>. Please have your items ready.
                  If you have questions, call us at{" "}
                  <a href={`tel:${businessConfig.phoneDial}`} className="font-black underline">
                    {businessConfig.phone}
                  </a>.
                </p>
              </div>

              <div className="bg-indigo-50 rounded-2xl p-4 text-left space-y-3">
                <p className="font-bold text-sm text-indigo-700">What happens next:</p>
                {[
                  "We will call you on " + form.phone + " to confirm pickup time",
                  "Our driver will arrive on " + form.date,
                  "Your clothes will be cleaned professionally",
                  "We deliver back to " + form.address,
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-gray-600">
                    <span className="w-5 h-5 rounded-full bg-indigo-200 text-indigo-700 flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    {item}
                  </div>
                ))}
              </div>

              {form.email && (
                <p className="text-xs text-gray-400">
                  📧 A confirmation email has been sent to {form.email}
                </p>
              )}

              <a
                href={`https://wa.me/${businessConfig.whatsapp}?text=Hi ${businessConfig.shortName}, I just booked a pickup. My name is ${form.name}, Order ID: ${savedOrder?.orderId}.`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full bg-green-600 text-white py-3 rounded-xl font-bold hover:bg-green-700 transition-colors text-sm"
              >
                💬 Follow Up on WhatsApp
              </a>

              <button
                onClick={onClose}
                className="w-full border border-gray-200 text-gray-600 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}